import type { CSSProperties } from "react";

import styled, { keyframes } from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes, setElevation, setFontVariant } from "@similarweb/styles";
import { Box, Stack, Flex } from "@similarweb/ui-components/foundations";

export const StyledFilesCard = styled.div`
    width: 100%;
    background-color: ${colorsPalettes.carbon[0]};
    border-radius: 6px;
    ${setElevation("Z1")}
`;

export const StyledFileRow = styled.div`
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 72px;
    padding-left: 12px;
    background-color: ${colorsPalettes.carbon[0]};
    border: 1px solid ${colorsPalettes.carbon[50]};
    border-radius: 8px;
`;

export const StyledFileIcon = styled(SWReactIcons)`
    margin-right: 20px;

    svg {
        width: 24px;
        height: 24px;
    }
`;

export const StyledFileName = styled.div`
    ${setFontVariant("body1", colorsPalettes.carbon[400])}
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    width: 100%;
    max-width: 646px;

    @media (max-width: 1280px) {
        max-width: 570px;
    }

    @media (max-width: 1180px) {
        max-width: 500px;
    }

    @media (max-width: 1100px) {
        max-width: 350px;
    }

    @media (max-width: 950px) {
        max-width: 200px;
    }
`;

export const StyledFileDate = styled.div`
    ${setFontVariant("caption", colorsPalettes.carbon[250])}
    line-height: 14px;
`;

const indeterminateSweep = keyframes`
    from {
        transform: translateX(-100%);
    }
    to {
        transform: translateX(250%);
    }
`;

/** The backend reports no progress, so the sweep stands for "still running", not for a completion share. */
export const StyledIndeterminateBar = styled.div`
    position: relative;
    width: 100%;
    height: 6px;
    overflow: hidden;
    border-radius: 3px;
    background-color: ${colorsPalettes.carbon[50]};

    &::after {
        content: "";
        position: absolute;
        inset: 0 auto 0 0;
        width: 40%;
        border-radius: inherit;
        background-color: ${colorsPalettes.blue[400]};
        animation: ${indeterminateSweep} 1.4s ease-in-out infinite;
    }

    @media (prefers-reduced-motion: reduce) {
        &::after {
            animation: none;
        }
    }
`;

/**
 * An enriching run owns the whole row: it has no counts to tabulate and nothing to download, so the
 * metric cells and the action are dropped and the name column spans to the right edge instead.
 */
export const StyledEnrichingBody = styled(Flex)`
    box-sizing: border-box;
    width: 100%;
    align-items: flex-start;
    gap: 0;
    padding: 16px 20px 16px 0;
`;

export const StyledEnrichingChip = styled(Flex)`
    flex-shrink: 0;
    align-items: center;
    gap: 6px;
    height: 24px;
    padding: 0 12px 0 8px;
    border-radius: 16px;
    background-color: ${colorsPalettes.blue[100]};
    ${setFontVariant("subtitle2", colorsPalettes.blue[400])}
`;

export const ENRICHING_LOADER_OPTIONS = {
    svg: {
        stroke: colorsPalettes.blue[400],
        strokeWidth: 2,
        r: 5,
        cx: "50%",
        cy: "50%",
    },
    style: {
        position: "initial" as CSSProperties["position"],
        width: 14,
        height: 14,
    },
};

export const StyledInfoCellFlex = styled(Flex)`
    box-sizing: border-box;
    padding: 0 20px;
    border-right: 1px solid ${colorsPalettes.carbon[50]};
`;

export const StyledMetaCell = styled(Stack)`
    box-sizing: border-box;
    padding: 0;
    border-right: none;
    width: 80px;
`;

export const StyledMetaValue = styled.div`
    ${setFontVariant("subtitle1", colorsPalettes.carbon[400])}
`;

export const StyledMetaCaption = styled.div`
    ${setFontVariant("caption", colorsPalettes.carbon[250])}
    line-height: 14px;
    text-align: center;
`;

export const StyledDownloadButton = styled.button`
    display: flex;
    align-items: center;
    padding: 0;
    background: none;
    border: none;
    cursor: pointer;

    & svg > path {
        fill: ${colorsPalettes.blue[400]};
    }

    &:disabled {
        cursor: default;
    }

    /* Keyed off aria-disabled, not :disabled — the in-flight download spinner sits inside a
       disabled button and must keep its own colour. carbon[100] is what ui-components' IconButton
       greys a disabled icon to. */
    &[aria-disabled="true"] {
        cursor: default;

        & svg > path {
            fill: ${colorsPalettes.carbon[100]};
        }
    }
`;

export const DOWNLOAD_LOADER_OPTIONS = {
    svg: {
        stroke: colorsPalettes.blue[400],
        strokeWidth: 2,
        r: 6,
        cx: "50%",
        cy: "50%",
    },
    style: {
        position: "initial" as CSSProperties["position"],
        width: 16,
        height: 16,
    },
};

export const StyledPaginationWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    padding: 8px 0;
    border-top: 1px solid ${colorsPalettes.carbon[100]};
`;

export const StyledEmptyStateWrapper = styled(Box)`
    text-align: center;
`;
